import { forwardRef } from "react";
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

import { cn } from "@/lib/cn";
import { useField } from "@/components/ui/Field";

/**
 * The field.
 *
 * White with a grey edge, and ringed in the
 * accent while it has focus. It was a filled well with no edge once,
 * when a panel was the only white thing on a grey page; the page is white
 * now, and a well in it would read as a disabled field.
 */
const FIELD_BASE =
  "w-full bg-surface px-3 text-base text-fg rounded-sm " +
  "border-0 ring-1 ring-inset ring-line-strong " +
  "transition-[background-color,box-shadow] duration-150 " +
  "hover:ring-ink-400 " +
  "focus:outline-none focus:ring-2 focus:ring-focus " +
  "disabled:bg-ink-50 disabled:text-fg-placeholder disabled:hover:ring-line-strong";

// Kept off the base, which the select shares: a select is never editable
// text, so `:read-only` matches every one of them and greyed them all.
const FIELD_READ_ONLY = "read-only:bg-ink-50 read-only:text-fg-muted";

// Marked important: `cn` joins classes and does not merge them, so without
// it the grey edge above and this red one would be settled by source order.
const FIELD_INVALID = "ring-alert-500! hover:ring-alert-600! focus:ring-alert-500!";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  invalid?: boolean;
  /** Static text pinned inside the trailing edge, e.g. a currency or unit. */
  suffix?: ReactNode;
  /** Renders the value in the identifier face (Emirates ID, IBAN, passport). */
  identifier?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, suffix, identifier = false, id, ...props },
  ref,
) {
  const field = useField();
  const isInvalid = invalid ?? field?.invalid ?? false;

  const input = (
    <input
      ref={ref}
      id={id ?? field?.inputId}
      aria-invalid={isInvalid || undefined}
      aria-describedby={field?.describedBy}
      className={cn(
        FIELD_BASE,
        FIELD_READ_ONLY,
        "h-9",
        identifier && "u-ident",
        isInvalid && FIELD_INVALID,
        suffix && "pr-12",
        className,
      )}
      {...props}
    />
  );

  if (!suffix) return input;

  return (
    <div className="relative">
      {input}
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-fg-subtle">
        {suffix}
      </span>
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, id, rows = 3, ...props },
  ref,
) {
  const field = useField();
  const isInvalid = invalid ?? field?.invalid ?? false;

  return (
    <textarea
      ref={ref}
      id={id ?? field?.inputId}
      rows={rows}
      aria-invalid={isInvalid || undefined}
      aria-describedby={field?.describedBy}
      className={cn(FIELD_BASE, FIELD_READ_ONLY, "resize-y py-2", isInvalid && FIELD_INVALID, className)}
      {...props}
    />
  );
});

export { FIELD_BASE, FIELD_INVALID };
